import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";
import MDBox from "components/MDBox";
import TypeBadge from "components/ReportUI/Typebadge";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { AccountsContext } from "context/AccountsContext";
import StopDistanceReportTable from "./stopdistancereporttable";
import { stopdistancereportendpoint } from "config";
import { getGeofenceendpoint, accountmy } from "config";
import DhundhooLoader from "../Loader/Loader";

function StopDistanceReport() {
  const [reportData, setReportData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [vehicleId, setVehicleId] = useState("");
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState("");

  const [geofences, setGeofences] = useState([]);
  const [selectedGeofence, setSelectedGeofence] = useState(null);
  const [orgName, setorgName] = useState("");
  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
    fetchGeofences();
    handleAccount();
  }, []);

  // Snackbar
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // Table Columns Setup
  const columns = [
    { Header: "Vehicle Number", accessor: "thingId", width: "15%" },
    {
      Header: "Journey Type", accessor: "journeyType", width: "15%",
      Cell: ({ value }) => (
        <TypeBadge
          value={value}
          config={{}}
          defaultConfig={{ color: "#1E40AF", bg: "#E9F1FF", icon: null }}
        />
      ),
    },
    { Header: "Route Name", accessor: "routeName", width: "15%" },
    { Header: "Stop Name", accessor: "stopName", width: "20%" },
    { Header: "Distance From", accessor: "distanceFrom", width: "20%" },
    { Header: "Distance To", accessor: "distanceTo", width: "20%" },
    {
      Header: "Distance (km)",
      accessor: "distance",
      width: "10%",
      align: "center",
      // ✅ FIX 1: Portal UI par distance ko 2 decimal points me round off kiya
      Cell: ({ value }) => {
        const parsedValue = parseFloat(value);
        return isNaN(parsedValue) ? "0.00" : parsedValue.toFixed(2);
      }
    },
  ];
  const today = new Date();

  // Fetch geofences
  const fetchGeofences = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = {
        method: "post",
        url: getGeofenceendpoint,
        headers: { "x-dhundhoo-session": token },
      };
      const response = await axios.request(config);
      setGeofences(response.data);
    } catch (error) {
      console.error("Geofence fetch error:", error);
    }
  };

  const handleAccount = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);

    try {
      const response = await axios.get(accountmy, {
        headers: {
          "x-dhundhoo-session": token,
        },
      });

      const orgName = response?.data.orgInfo?.orgName || "";
      setorgName(orgName);
    } catch (error) {
      console.error("API Error in handleAccount:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Stop Distance Report
  const fetchReport = async () => {
    if (!vehicleId || !selectedGeofence) {
      setErrorMessage("Please select vehicle and geofence");
      openErrorSB();
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    setErrorMessage("");
    const formatDateOnly = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    try {
      const data = {
        date: formatDateOnly(today),
        zone: "Asia/Kolkata",
        things: vehicleId,
        geofence: [selectedGeofence.tags[0]],
        orgName: orgName,
        geoFenceName: selectedGeofence.tags[0],
        geoLat: selectedGeofence.data.location.latitude,
        geoLong: selectedGeofence.data.location.longitude,
      };

      const config = {
        method: "post",
        url: stopdistancereportendpoint,
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data,
      };

      const response = await axios.request(config);
      setReportData(response.data);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading Stop Distance Report");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setVehicleId("");
    setReportData([]);
    setErrorMessage("");
    setDate(new Date());
    setSelectedGeofence(null);
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      setErrorMessage("No data to export");
      openErrorSB();
      return;
    }

    const now = new Date();
    const formattedDate = `${now.getFullYear()}-${(now.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;

    const headers = [
      "Vehicle Number",
      "Journey Type",
      "Route Name",
      "Stop Name",
      "Distance From",
      "Distance To",
      "Distance (km)"
    ];

    const rows = reportData.map((row) => {
      const vehicleNum = `"${row.thingId || ''}"`;
      const jType = `"${row.journeyType || ''}"`;
      const rName = `"${row.routeName || ''}"`;
      const sName = `"${(row.stopName || '').replace(/"/g, '""')}"`;
      const distFrom = `"${(row.distanceFrom || '').replace(/"/g, '""')}"`;
      const distTo = `"${(row.distanceTo || '').replace(/"/g, '""')}"`;

      // ✅ FIX 2: CSV File export me bhi 2 digit formatting restrict ki
      const parsedDistance = parseFloat(row.distance);
      const distValue = `"${!isNaN(parsedDistance) ? parsedDistance.toFixed(2) : '0.00'}"`;

      return [vehicleNum, jType, rName, sName, distFrom, distTo, distValue].join(",");
    });

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `StopDistanceReport_${formattedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2}>
                    {/* Vehicle Select */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        options={things || []}
                        getOptionLabel={(option) => option.thingId || ""}
                        filterOptions={(options, { inputValue }) => {
                          const searchValue = inputValue.toLowerCase().trim();

                          return options.filter((option) => {
                            const name = (option.name || "").toLowerCase();
                            const nickName = (option.nickName || "").toLowerCase();
                            const thingId = (option.thingId || "").toLowerCase();

                            return (
                              name.includes(searchValue) ||
                              nickName.includes(searchValue) ||
                              thingId.includes(searchValue)
                            );
                          });
                        }}
                        value={things.find((t) => t.thingId === vehicleId) || null}
                        onChange={(event, newValue) => {
                          setVehicleId(newValue ? newValue.thingId : "");
                        }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <span>{option.nickName || option.name || "-"}</span>
                            <span style={{ color: "#666" }}>{option.thingId}</span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Vehicle"
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#FB8C00",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#FB8C00",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Geofence Select */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        options={geofences}
                        getOptionLabel={(option) => option.tags && option.tags[0] ? option.tags[0] : ""}
                        value={selectedGeofence}
                        onChange={(event, newValue) => setSelectedGeofence(newValue)}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select Geofence" fullWidth size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#FB8C00",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#FB8C00",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton fullWidth variant="gradient" color="warning" size="small" onClick={fetchReport} disabled={loading}>
                        {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                      </MDButton>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton fullWidth variant="outlined" color="error" size="small" onClick={clearFilters} disabled={loading}>
                        CLEAR
                      </MDButton>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading} />
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              {/* Loader or Table */}
              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <DhundhooLoader />
                ) : (
                  <StopDistanceReportTable
                    table={{ columns, rows: reportData }}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Snackbars */}
      <MDSnackbar color="success" icon="check" title="Stop Distance Report" content="Data Loaded Successfully" open={successSB} onClose={closeSuccessSB} close={closeSuccessSB} bgWhite />
      <MDSnackbar color="error" icon="warning" title="Stop Distance Report" content="Failed to load report" open={errorSB} onClose={closeErrorSB} close={closeErrorSB} bgWhite />
    </DashboardLayout>
  );
}

export default StopDistanceReport;